/**
 * AI-Helpful Structured Logger
 * - JSON lines for parsing, filtering, correlation
 * - Same structure in browser (console) and Node (stdout/file)
 * - Env: LOG_LEVEL (debug|info|warn|error), AI_LOG_FILE (optional path)
 */

const LEVELS = { debug: 0, info: 1, warn: 2, error: 3 };

function getLevel() {
  try {
    if (typeof process !== 'undefined' && process.env?.LOG_LEVEL) {
      const l = String(process.env.LOG_LEVEL).toLowerCase();
      return LEVELS[l] ?? LEVELS.info;
    }
  } catch (_) {}
  return LEVELS.info;
}

const currentLevel = getLevel();
const isBrowser = typeof window !== 'undefined';
const logFilePath = typeof process !== 'undefined' ? process.env.AI_LOG_FILE : null;

function writeLine(obj) {
  const line = JSON.stringify(obj) + '\n';
  if (isBrowser) {
    const { lvl } = obj;
    if (lvl === 'error') console.error(line.trim());
    else if (lvl === 'warn') console.warn(line.trim());
    else console.log(line.trim());
  } else if (typeof process !== 'undefined' && process.stdout) {
    process.stdout.write(line);
    if (logFilePath) {
      try {
        require('fs').appendFileSync(logFilePath, line);
      } catch (e) {
        // ignore file write errors
      }
    }
  } else {
    // Edge runtime fallback
    console.log(line.trim());
  }
}

function makeLogger(component, defaults = {}) {
  function log(lvl, msg, data = {}) {
    const levelNum = LEVELS[lvl];
    if (levelNum < currentLevel) return;

    const base = {
      ts: Date.now(),
      lvl,
      cmp: component,
      msg,
      ...defaults,
      ...data,
    };

    // Strip undefined values for cleaner JSON
    const clean = {};
    for (const [k, v] of Object.entries(base)) {
      if (v !== undefined) clean[k] = v;
    }

    writeLine(clean);
  }

  return {
    debug: (msg, data) => log('debug', msg, data),
    info: (msg, data) => log('info', msg, data),
    warn: (msg, data) => log('warn', msg, data),
    error: (msg, data) => log('error', msg, data),
    /** Returns a new logger with pre-bound fields merged into every call. */
    child: (childDefaults) => makeLogger(component, { ...defaults, ...childDefaults }),
  };
}

export function createLogger(component) {
  return makeLogger(component);
}
